import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, Clock, LogOut, Wifi, WifiOff, ChefHat, Trash2, Archive,
  PackageCheck, Drumstick, Pizza as PizzaIcon, UtensilsCrossed, Sandwich,
  Printer, X, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import OrderChatPanel from "@/components/OrderChatPanel";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { WebSocketProvider, useWebSocket } from "@/contexts/WebSocketContext";
import { BRAND_COLORS } from "@/lib/menuData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";

function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch (e) {
    console.error("Audio failed:", e);
  }
}

function getTimeSince(isoString) {
  if (!isoString) return "";
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
}

// ============ Concept ordering & display helpers ============
// Sort order for items inside a kitchen ticket:
//   1. Krispy Krunchy Chicken (chicken)
//   2. Hunt Brothers Pizza (pizza)
//   3. Lunch Menu (lunch)
//   4. Burgers / others (everything else)
const CONCEPT_ORDER = { chicken: 1, pizza: 2, lunch: 3, burgers: 4 };

const CONCEPT_META = {
  chicken: {
    label: "Krispy Krunchy Chicken",
    color: BRAND_COLORS.sunsetOrange,
    bg: "#FFF4E6",
    Icon: Drumstick,
  },
  pizza: {
    label: "Hunt Brothers Pizza",
    color: BRAND_COLORS.deepTeal,
    bg: "#E6F2F2",
    Icon: PizzaIcon,
  },
  lunch: {
    label: "Daily Specials",
    color: "#2E7D32",
    bg: "#E8F5E9",
    Icon: UtensilsCrossed,
  },
  burgers: {
    label: "Burgers",
    color: BRAND_COLORS.tropicalAqua,
    bg: "#E6F7F7",
    Icon: Sandwich,
  },
  other: {
    label: "Other",
    color: "#64748B",
    bg: "#F1F5F9",
    Icon: ChefHat,
  },
};

// Categories that should always be classified as "burgers" on a kitchen ticket,
// regardless of their parent concept (lunch_burger items have concept="lunch").
const BURGER_CATEGORIES = ["lunch_burger", "hamburger", "cheeseburger"];

const conceptKey = (item) => {
  // Treat any burger-category item as a Burgers ticket section, even when its
  // parent concept is "lunch". This matches kitchen-side sort order:
  // Chicken -> Pizza -> Lunch -> Burgers.
  const cat = (item.category || "").toLowerCase();
  if (BURGER_CATEGORIES.indexOf(cat) !== -1) return "burgers";
  const c = (item.concept || "").toLowerCase();
  if (CONCEPT_META[c]) return c;
  // Some lunch_burger items may have concept="lunch"; hamburger/cheeseburger may be "burgers"
  return "other";
};

const sortItemsByConcept = (items) => {
  return [...items].sort((a, b) => {
    const ao = CONCEPT_ORDER[conceptKey(a)] ?? 99;
    const bo = CONCEPT_ORDER[conceptKey(b)] ?? 99;
    if (ao !== bo) return ao - bo;
    return (a.name || "").localeCompare(b.name || "");
  });
};

const groupItemsByConcept = (items) => {
  const sorted = sortItemsByConcept(items);
  const groups = {};
  for (const it of sorted) {
    const k = conceptKey(it);
    if (!groups[k]) groups[k] = [];
    groups[k].push(it);
  }
  // Return as ordered array
  const order = ["chicken", "pizza", "lunch", "burgers", "other"];
  return order.filter((k) => groups[k]).map((k) => ({ key: k, items: groups[k] }));
};

// Human-readable label for customization keys
const CUSTOMIZATION_LABELS = {
  toppings: "Toppings",
  left_half: "Left Half",
  right_half: "Right Half",
  premium_toppings: "Premium Toppings",
  extra_cheese: "Extra Cheese",
  crust: "Crust",
  bacon: "Bacon",
  jalapeno: "Jalapeño",
  cheese_slice: "Cheese Slice",
  extra_patties: "Extra Patties",
  meal: "Made it a Meal",
  side: "Side",
  drink: "Drink",
  dipping_sauce: "Dipping Sauce",
  dipping_sauces: "Dipping Sauces",
  sauce: "Sauce",
  sauces: "Sauces",
  flavor: "Flavor",
  hot_sauce: "Hot Sauce",
  honey: "Honey",
  pieces: "Pieces",
  piece_count: "Pieces",
  size: "Size",
  spice: "Spice Level",
  notes: "Notes",
  special_instructions: "Special Instructions",
};

const TOPPING_KEYS = new Set([
  "toppings", "left_half", "right_half", "premium_toppings", "extra_cheese",
]);

const humanizeKey = (k) =>
  CUSTOMIZATION_LABELS[k] ||
  k
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const formatValue = (v) => {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object" && v !== null) {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

// Render the customization block for a single item — toppings highlighted.
function ItemCustomizations({ customizations }) {
  if (!customizations) return null;
  const entries = Object.entries(customizations).filter(([, v]) =>
    v !== null && v !== undefined && v !== "" && v !== false
  );
  if (entries.length === 0) return null;

  // Filter out flags that aren't useful for kitchen
  const filtered = entries.filter(([k]) =>
    !["isLimitedEdition", "isSpecialty"].includes(k)
  );
  if (filtered.length === 0) return null;

  const toppingEntries = filtered.filter(([k]) => TOPPING_KEYS.has(k));
  const otherEntries = filtered.filter(([k]) => !TOPPING_KEYS.has(k));

  return (
    <div className="mt-1.5 ml-4 space-y-1">
      {toppingEntries.length > 0 && (
        <div
          className="rounded-md px-2 py-1.5 border"
          style={{ backgroundColor: "#FFFBEB", borderColor: "#FDE68A" }}
        >
          {toppingEntries.map(([k, v]) => (
            <div key={k} className="text-[12px] leading-snug">
              <span className="font-bold uppercase tracking-wide text-[10px]" style={{ color: "#92400E" }}>
                {humanizeKey(k)}:
              </span>{" "}
              <span className="font-semibold" style={{ color: "#78350F" }}>
                {formatValue(v)}
              </span>
            </div>
          ))}
        </div>
      )}
      {otherEntries.length > 0 && (
        <div className="text-[11px] space-y-0.5">
          {otherEntries.map(([k, v]) => (
            <div key={k} style={{ color: BRAND_COLORS.darkSlate + "B0" }}>
              <span className="font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>
                {humanizeKey(k)}:
              </span>{" "}
              {formatValue(v)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderTicket({ order, onAccept, onDecline, onComplete, onDelete, onPickedUp, onPrint, isDeleted, isReady }) {
  // Backend: new -> preparing -> ready -> picked_up
  const isPending = order.status === "new" || order.status === "pending";
  const isAccepted = order.status === "preparing" || order.status === "accepted";

  const borderColor = isDeleted
    ? "#9CA3AF"
    : isReady
    ? "#16A34A"
    : isPending
    ? "#EF4444"
    : "#3B82F6";

  const statusLabel = isDeleted
    ? "DELETED"
    : isReady
    ? "READY FOR PICKUP"
    : isPending
    ? "NEW"
    : "IN PROGRESS";

  const statusBadge = isDeleted
    ? "bg-gray-100 text-gray-600"
    : isReady
    ? "bg-green-100 text-green-700"
    : isPending
    ? "bg-red-100 text-red-600"
    : "bg-blue-100 text-blue-600";

  const grouped = useMemo(() => groupItemsByConcept(order.items || []), [order.items]);

  return (
    <div
      data-testid={`order-ticket-${order.id}`}
      className="bg-white rounded-lg overflow-hidden transition-all hover:shadow-md shadow-sm"
      style={{ borderLeft: `4px solid ${borderColor}`, opacity: isDeleted ? 0.7 : 1 }}
    >
      {/* Ticket Header */}
      <div className="px-3 py-2 flex items-center justify-between border-b" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>
            #{order.id.slice(0, 6).toUpperCase()}
          </span>
          <Badge
            data-testid={`order-status-${order.id}`}
            className={`text-[10px] px-1.5 py-0 ${statusBadge}`}
          >
            {statusLabel}
          </Badge>
        </div>
        <div className="flex items-center gap-1" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
          <Clock className="h-3 w-3" />
          <span className="font-mono text-xs">{getTimeSince(order.created_at)}</span>
        </div>
      </div>

      {/* Customer Info */}
      <div className="px-3 py-2 border-b" style={{ borderColor: "#E5E7EB", backgroundColor: "#FAFAFA" }}>
        <p className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
          <span className="font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>{order.customer_name}</span>
          {" "} • {order.phone_number || order.table_number || "N/A"}
        </p>
        <p className="text-[10px] font-mono mt-0.5" style={{ color: BRAND_COLORS.sunsetOrange }}>TO-GO</p>
        {order.notes && (
          <div className="mt-1.5 px-2 py-1 rounded" style={{ backgroundColor: "#FEF3C7", borderLeft: "2px solid #F59E0B" }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#78350F" }}>Note</p>
            <p className="text-[11px] leading-snug" style={{ color: "#78350F" }}>{order.notes}</p>
          </div>
        )}
      </div>

      {/* Items grouped by concept (Chicken → Pizza → Lunch → Burgers/Other) */}
      <div className="px-3 py-2 space-y-2.5">
        {grouped.map(({ key, items }) => {
          const meta = CONCEPT_META[key] || CONCEPT_META.other;
          const Icon = meta.Icon;
          return (
            <div key={key} className="rounded-md overflow-hidden border" style={{ borderColor: "#F1F5F9" }}>
              {/* Concept band */}
              <div
                className="px-2 py-1 flex items-center gap-1.5"
                style={{ backgroundColor: meta.bg }}
              >
                <Icon className="h-3 w-3" style={{ color: meta.color }} />
                <span
                  className="text-[10px] font-mono font-bold uppercase tracking-wider"
                  style={{ color: meta.color }}
                >
                  {meta.label}
                </span>
                <span className="text-[10px] font-mono ml-auto" style={{ color: meta.color + "AA" }}>
                  {items.reduce((s, it) => s + (it.quantity || 1), 0)} item{items.length > 1 ? "s" : ""}
                </span>
              </div>

              {/* Items in this concept */}
              <div className="px-2 py-1.5 space-y-1.5">
                {items.map((item, i) => (
                  <div key={i} className="text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold leading-snug" style={{ color: BRAND_COLORS.darkSlate }}>
                        {item.quantity}× {item.name}
                      </span>
                      <span className="font-mono text-xs flex-shrink-0" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                        ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                      </span>
                    </div>
                    <ItemCustomizations customizations={item.customizations} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div className="px-3 py-1.5 border-t" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex justify-between items-center">
          <span className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "66" }}>Total</span>
          <span className="font-mono font-bold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>
            ${order.total.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Actions */}
      {!isDeleted && (
        <div className="px-3 py-2 border-t space-y-2" style={{ borderColor: "#E5E7EB", backgroundColor: "#FAFAFA" }}>
          {isPending && (
            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid={`decline-order-${order.id}`}
                variant="outline"
                className="h-10 rounded-lg font-mono font-bold uppercase tracking-wider text-xs"
                style={{ borderColor: "#EF4444", color: "#B91C1C", backgroundColor: "#FEF2F2" }}
                onClick={() => onDecline?.(order.id, order.customer_name)}
              >
                <X className="h-4 w-4 mr-1" /> DECLINE
              </Button>
              <Button
                data-testid={`accept-order-${order.id}`}
                className="h-10 rounded-lg font-mono font-bold uppercase tracking-wider text-xs text-white"
                style={{ backgroundColor: "#22C55E" }}
                onClick={() => onAccept(order.id)}
              >
                <Check className="h-4 w-4 mr-1" /> ACCEPT
              </Button>
            </div>
          )}
          {isAccepted && (
            <Button
              data-testid={`complete-order-${order.id}`}
              className="w-full h-10 rounded-lg font-mono font-bold uppercase tracking-wider text-xs text-white"
              style={{ backgroundColor: "#2563EB" }}
              onClick={() => onComplete(order.id)}
            >
              <ChefHat className="h-4 w-4 mr-1" /> MARK READY
            </Button>
          )}
          {isReady && (
            <Button
              data-testid={`picked-up-order-${order.id}`}
              className="w-full h-10 rounded-lg font-mono font-bold uppercase tracking-wider text-xs text-white"
              style={{ backgroundColor: "#16A34A" }}
              onClick={() => onPickedUp(order.id)}
            >
              <PackageCheck className="h-4 w-4 mr-1" /> MARK PICKED UP
            </Button>
          )}
          {!isReady && !isDeleted && (
            <Button
              data-testid={`print-order-${order.id}`}
              variant="outline"
              size="sm"
              className="w-full h-8 rounded-lg font-mono text-xs"
              style={{ borderColor: "#E5E7EB", color: BRAND_COLORS.darkSlate + "AA" }}
              onClick={() => onPrint?.(order.id)}
            >
              <Printer className="h-3.5 w-3.5 mr-1" /> REPRINT
            </Button>
          )}
          {!isReady && (
            <Button
              data-testid={`delete-order-${order.id}`}
              variant="outline"
              className="w-full h-8 rounded-lg font-mono text-xs"
              style={{ borderColor: "#E5E7EB", color: BRAND_COLORS.darkSlate + "99" }}
              onClick={() => onDelete(order.id, order.customer_name)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> DELETE
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function KitchenContent() {
  const [orders, setOrders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [deletedOrders, setDeletedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, orderId: null, customerName: "" });
  const [declineDialog, setDeclineDialog] = useState({ open: false, orderId: null, customerName: "", reason: "" });
  // Chat inbox — Phase 15
  const [messageOrders, setMessageOrders] = useState([]); // [{order_id, unread, last_at, last_text, customer_name}]
  const [chatOrderId, setChatOrderId] = useState(null); // currently-open chat
  const [inboxOpen, setInboxOpen] = useState(false);

  // Repeating chime: loop every 30s while there's an unread customer message older than 60s
  useEffect(() => {
    const totalUnread = messageOrders.reduce((sum, m) => sum + (m.unread || 0), 0);
    // Skip if nothing unread OR the kitchen is actively reading a thread
    if (totalUnread === 0 || (chatOrderId && inboxOpen)) return undefined;
    // Find the oldest unread message time
    const oldestAt = messageOrders
      .filter((m) => m.unread > 0 && m.last_at)
      .map((m) => new Date(m.last_at).getTime())
      .sort((a, b) => a - b)[0];
    if (!oldestAt) return undefined;
    const staleFor = Date.now() - oldestAt;
    // Only start the loop once the message has been unread for >= 60s
    const kickoffDelay = Math.max(0, 60_000 - staleFor);
    const interval = setInterval(() => playChime(), 30_000);
    const kickoff = setTimeout(() => playChime(), kickoffDelay);
    return () => {
      clearInterval(interval);
      clearTimeout(kickoff);
    };
  }, [messageOrders, chatOrderId, inboxOpen]);
  const { token, logout, getAuthHeaders } = useAuth();
  const { lastMessage, connected, connect } = useWebSocket();
  const navigate = useNavigate();
  const prevMessageRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    try {
      const [activeRes, readyRes, deletedRes] = await Promise.all([
        axios.get(`${API}/orders/active`, { headers: getAuthHeaders() }),
        axios.get(`${API}/orders/ready`, { headers: getAuthHeaders() }),
        axios.get(`${API}/orders/deleted`, { headers: getAuthHeaders() }),
      ]);
      setOrders(activeRes.data);
      setReadyOrders(readyRes.data);
      setDeletedOrders(deletedRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        navigate("/kitchen/login");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, logout, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/kitchen/login");
      return;
    }
    fetchOrders();
    connect();
    // Fetch initial unread messages summary
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/messages/unread-summary`, { headers: getAuthHeaders() });
        // Enrich with customer_name by joining against active orders (best-effort)
        setMessageOrders(res.data || []);
      } catch (e) {
        /* ignore */
      }
    })();
  }, [token, navigate, fetchOrders, connect, getAuthHeaders]);

  useEffect(() => {
    if (!lastMessage || lastMessage === prevMessageRef.current) return;
    prevMessageRef.current = lastMessage;

    if (lastMessage.type === "new_order") {
      setOrders((prev) => [...prev, lastMessage.order]);
      playChime();
      toast.info(`New order from ${lastMessage.order.customer_name}`);
    } else if (lastMessage.type === "order_updated") {
      const updated = lastMessage.order;
      if (updated.status === "ready") {
        // Move from active to ready
        setOrders((prev) => prev.filter((o) => o.id !== updated.id));
        setReadyOrders((prev) => {
          if (prev.some((o) => o.id === updated.id)) {
            return prev.map((o) => (o.id === updated.id ? updated : o));
          }
          return [...prev, updated];
        });
        playChime();
        toast.success(`Order #${updated.id.slice(0, 6).toUpperCase()} is ready for pickup`);
      } else if (updated.status === "picked_up" || updated.status === "completed") {
        // Remove from both lists
        setOrders((prev) => prev.filter((o) => o.id !== updated.id));
        setReadyOrders((prev) => prev.filter((o) => o.id !== updated.id));
      } else if (updated.status === "declined") {
        // Remove from active - order was declined by kitchen
        setOrders((prev) => prev.filter((o) => o.id !== updated.id));
      } else {
        setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      }
    } else if (lastMessage.type === "order_deleted") {
      const deleted = lastMessage.order;
      setOrders((prev) => prev.filter((o) => o.id !== deleted.id));
      setReadyOrders((prev) => prev.filter((o) => o.id !== deleted.id));
      setDeletedOrders((prev) => [deleted, ...prev]);
    } else if (lastMessage.type === "new_message") {
      const msg = lastMessage.message;
      const orderMeta = lastMessage.order || {};
      // Only alert on messages from the customer (staff writes don't need a chime)
      if (msg?.sender === "customer") {
        playChime();
        toast.info(`New message from ${orderMeta.customer_name || "customer"}`, {
          description: msg.text ? msg.text.slice(0, 80) : "📎 Image attached",
          action: {
            label: "Reply",
            onClick: () => { setChatOrderId(msg.order_id); setInboxOpen(true); },
          },
        });
      }
      // Update inbox rollup: bump unread + move to top
      setMessageOrders((prev) => {
        const isCustomer = msg?.sender === "customer";
        const existing = prev.find((o) => o.order_id === msg.order_id);
        const next = prev.filter((o) => o.order_id !== msg.order_id);
        const isViewingThisOrder = chatOrderId === msg.order_id && inboxOpen;
        return [
          {
            order_id: msg.order_id,
            unread: existing
              ? (isCustomer && !isViewingThisOrder ? existing.unread + 1 : existing.unread)
              : (isCustomer && !isViewingThisOrder ? 1 : 0),
            last_at: msg.created_at,
            last_text: msg.text || (msg.image_data_url ? "📎 Image" : ""),
            customer_name: orderMeta.customer_name || existing?.customer_name,
          },
          ...next,
        ];
      });
    }
  }, [lastMessage, chatOrderId, inboxOpen]);

  const handleAccept = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/accept`, {}, { headers: getAuthHeaders() });
      toast.success("Order accepted & receipt printed");
    } catch (e) {
      toast.error("Failed to accept order");
    }
  };

  const handleDecline = (orderId, customerName) => {
    setDeclineDialog({ open: true, orderId, customerName, reason: "" });
  };

  const handleDeclineConfirm = async () => {
    const { orderId, reason } = declineDialog;
    try {
      await axios.put(
        `${API}/orders/${orderId}/decline`,
        { reason: reason || null },
        { headers: getAuthHeaders() }
      );
      toast.success("Order declined");
      setDeclineDialog({ open: false, orderId: null, customerName: "", reason: "" });
    } catch (e) {
      toast.error("Failed to decline order");
    }
  };

  const handleComplete = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/complete`, {}, { headers: getAuthHeaders() });
      toast.success("Order marked ready for pickup");
    } catch (e) {
      toast.error("Failed to complete order");
    }
  };

  const handlePickedUp = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/picked-up`, {}, { headers: getAuthHeaders() });
      toast.success("Order picked up");
    } catch (e) {
      toast.error("Failed to mark as picked up");
    }
  };

  const handlePrint = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/print`, {}, { headers: getAuthHeaders() });
      toast.success("Ticket sent to printer");
    } catch (e) {
      toast.error("Failed to send to printer");
    }
  };

  const handleDeleteClick = (orderId, customerName) => {
    setDeleteDialog({ open: true, orderId, customerName });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.put(`${API}/orders/${deleteDialog.orderId}/delete`, {}, { headers: getAuthHeaders() });
      toast.success("Order deleted");
      setDeleteDialog({ open: false, orderId: null, customerName: "" });
    } catch (e) {
      toast.error("Failed to delete order");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/kitchen/login");
  };

  // Backend uses: new -> preparing -> ready
  const pendingOrders = orders.filter((o) => o.status === "new" || o.status === "pending");
  const acceptedOrders = orders.filter((o) => o.status === "preparing" || o.status === "accepted");

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.sandWhite }} data-testid="kitchen-dashboard">
      {/* Header */}
      <header className="h-14 border-b flex items-center justify-between px-4" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full object-cover" />
          <h1 className="text-lg font-extrabold text-white" style={{ fontFamily: "Montserrat" }}>
            Kitchen Display
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            {connected ? (
              <Wifi className="h-4 w-4 text-green-400" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-400" />
            )}
            <span className="text-xs font-mono text-white/70">
              {connected ? "LIVE" : "OFFLINE"}
            </span>
          </div>
          <Badge className="font-mono text-xs text-white" style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}>
            {pendingOrders.length} pending
          </Badge>
          <Badge className="font-mono text-xs text-white" style={{ backgroundColor: "#16A34A" }}>
            {readyOrders.length} ready
          </Badge>
          <Button
            data-testid="messages-inbox-btn"
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white relative"
            onClick={() => setInboxOpen(true)}
          >
            <MessageCircle className="h-5 w-5" />
            {messageOrders.some((m) => m.unread > 0) && (
              <span
                data-testid="inbox-unread-dot"
                className="absolute top-0 right-0 bg-yellow-400 text-slate-900 text-[9px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center"
              >
                {messageOrders.reduce((sum, m) => sum + (m.unread || 0), 0)}
              </span>
            )}
          </Button>
          <Button
            data-testid="kitchen-logout-btn"
            variant="ghost"
            size="sm"
            className="text-white/70 hover:text-white"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b px-4 py-2" style={{ backgroundColor: "white" }}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger
              data-testid="tab-active"
              value="active"
              className="text-xs font-mono data-[state=active]:bg-white"
              style={{ fontFamily: "Montserrat" }}
            >
              <ChefHat className="h-3.5 w-3.5 mr-1" /> Active Orders ({pendingOrders.length + acceptedOrders.length})
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-ready"
              value="ready"
              className="text-xs font-mono data-[state=active]:bg-white"
              style={{ fontFamily: "Montserrat" }}
            >
              <PackageCheck className="h-3.5 w-3.5 mr-1" /> Ready for Pickup ({readyOrders.length})
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-deleted"
              value="deleted"
              className="text-xs font-mono data-[state=active]:bg-white"
              style={{ fontFamily: "Montserrat" }}
            >
              <Archive className="h-3.5 w-3.5 mr-1" /> Deleted ({deletedOrders.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Active Orders View */}
      {tab === "active" && (
        <div className="flex h-[calc(100vh-104px)]">
          {/* Pending (New) Orders */}
          <div className="flex-1 border-r">
            <div className="h-10 border-b flex items-center px-4" style={{ backgroundColor: "#FEE2E2" }}>
              <span className="font-mono font-bold text-xs uppercase tracking-wider" style={{ color: "#DC2626", fontFamily: "Montserrat" }}>
                New Orders ({pendingOrders.length})
              </span>
            </div>
            <ScrollArea className="h-[calc(100vh-144px)]">
              <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {pendingOrders.map((order) => (
                  <OrderTicket
                    key={order.id}
                    order={order}
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                    onComplete={handleComplete}
                    onDelete={handleDeleteClick}
                    onPickedUp={handlePickedUp}
                    onPrint={handlePrint}
                  />
                ))}
                {pendingOrders.length === 0 && (
                  <div className="col-span-2 text-center py-12">
                    <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "66" }}>No pending orders</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Accepted (In Progress) Orders */}
          <div className="flex-1">
            <div className="h-10 border-b flex items-center px-4" style={{ backgroundColor: "#DBEAFE" }}>
              <span className="font-mono font-bold text-xs uppercase tracking-wider" style={{ color: "#2563EB", fontFamily: "Montserrat" }}>
                In Progress ({acceptedOrders.length})
              </span>
            </div>
            <ScrollArea className="h-[calc(100vh-144px)]">
              <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {acceptedOrders.map((order) => (
                  <OrderTicket
                    key={order.id}
                    order={order}
                    onAccept={handleAccept}
                    onComplete={handleComplete}
                    onDelete={handleDeleteClick}
                    onPickedUp={handlePickedUp}
                    onPrint={handlePrint}
                  />
                ))}
                {acceptedOrders.length === 0 && (
                  <div className="col-span-2 text-center py-12">
                    <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "66" }}>No orders in progress</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Ready for Pickup View */}
      {tab === "ready" && (
        <ScrollArea className="h-[calc(100vh-104px)]">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-mono" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                Hand these to the customer and tap <strong style={{ color: "#16A34A" }}>MARK PICKED UP</strong> to clear.
              </p>
              <Badge className="font-mono text-xs text-white" style={{ backgroundColor: "#16A34A" }}>
                {readyOrders.length} waiting
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {readyOrders.map((order) => (
                <OrderTicket
                  key={order.id}
                  order={order}
                  onAccept={() => {}}
                  onComplete={() => {}}
                  onDelete={handleDeleteClick}
                  onPickedUp={handlePickedUp}
                  isReady
                />
              ))}
              {readyOrders.length === 0 && (
                <div className="col-span-3 text-center py-16">
                  <PackageCheck className="h-10 w-10 mx-auto mb-2" style={{ color: BRAND_COLORS.darkSlate + "33" }} />
                  <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                    No orders waiting for pickup right now.
                  </p>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      )}

      {/* Deleted Orders View */}
      {tab === "deleted" && (
        <ScrollArea className="h-[calc(100vh-104px)]">
          <div className="p-4">
            <p className="text-xs mb-4 font-mono" style={{ color: BRAND_COLORS.darkSlate + "66" }}>Showing deleted orders (most recent first)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {deletedOrders.map((order) => (
                <OrderTicket
                  key={order.id}
                  order={order}
                  onAccept={() => {}}
                  onComplete={() => {}}
                  onDelete={() => {}}
                  onPickedUp={() => {}}
                  isDeleted
                />
              ))}
              {deletedOrders.length === 0 && (
                <div className="col-span-3 text-center py-12">
                  <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "66" }}>No deleted orders</p>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, orderId: null, customerName: "" })}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: BRAND_COLORS.darkSlate, fontFamily: "Montserrat" }}>Delete Order?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              Are you sure you want to delete the order for <strong style={{ color: BRAND_COLORS.darkSlate }}>{deleteDialog.customerName}</strong>?
              This order will be moved to the Deleted Orders list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-order-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteConfirm}
            >
              Delete Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Decline Confirmation Dialog */}
      <AlertDialog
        open={declineDialog.open}
        onOpenChange={(o) => !o && setDeclineDialog({ open: false, orderId: null, customerName: "", reason: "" })}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: BRAND_COLORS.darkSlate, fontFamily: "Montserrat" }}>
              Decline Order?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              Decline the order for{" "}
              <strong style={{ color: BRAND_COLORS.darkSlate }}>{declineDialog.customerName}</strong>?
              The customer will be notified immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-2">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND_COLORS.darkSlate + "AA" }}>
              Reason (optional)
            </label>
            <div className="grid grid-cols-2 gap-2 mt-2 mb-2">
              {["Out of stock", "Too busy", "Store closed", "Payment issue"].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  data-testid={`decline-preset-${preset.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-xs rounded-md border px-2 py-1.5 text-left transition-colors hover:bg-slate-50"
                  style={{
                    borderColor: declineDialog.reason === preset ? "#EF4444" : "#E5E7EB",
                    color: declineDialog.reason === preset ? "#B91C1C" : BRAND_COLORS.darkSlate,
                    backgroundColor: declineDialog.reason === preset ? "#FEF2F2" : "white",
                  }}
                  onClick={() => setDeclineDialog((d) => ({ ...d, reason: preset }))}
                >
                  {preset}
                </button>
              ))}
            </div>
            <textarea
              data-testid="decline-reason-input"
              value={declineDialog.reason}
              onChange={(e) => setDeclineDialog((d) => ({ ...d, reason: e.target.value.slice(0, 200) }))}
              placeholder="Add a custom reason..."
              rows={2}
              maxLength={200}
              className="w-full rounded-md bg-slate-50 border border-slate-200 text-sm px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-red-300"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-decline-order-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeclineConfirm}
            >
              Decline Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Messages Inbox Sheet */}
      <Sheet open={inboxOpen && !chatOrderId} onOpenChange={(o) => { if (!o) setInboxOpen(false); }}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b bg-white shrink-0">
            <SheetTitle className="text-base flex items-center gap-2">
              <MessageCircle className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />
              Messages
            </SheetTitle>
            <p className="text-[11px] text-slate-500">Tap an order to reply</p>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0" data-testid="inbox-list">
            {messageOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <MessageCircle className="h-8 w-8 text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No messages yet</p>
              </div>
            ) : (
              messageOrders.map((m) => {
                const activeOrder = [...orders, ...readyOrders].find((o) => o.id === m.order_id);
                const name = activeOrder?.customer_name || m.customer_name || `Order ${m.order_id.slice(0, 6)}`;
                return (
                  <button
                    key={m.order_id}
                    data-testid={`inbox-item-${m.order_id}`}
                    className="w-full flex items-start gap-3 px-4 py-3 border-b hover:bg-slate-50 text-left"
                    onClick={() => { setChatOrderId(m.order_id); }}
                  >
                    <div
                      className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-sm font-bold text-white"
                      style={{ backgroundColor: BRAND_COLORS.tropicalAqua }}
                    >
                      {(name[0] || "?").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold truncate" style={{ color: BRAND_COLORS.darkSlate }}>
                          {name}
                        </p>
                        {m.unread > 0 && (
                          <span className="bg-yellow-400 text-slate-900 text-[10px] font-bold rounded-full px-2 py-0.5">
                            {m.unread}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{m.last_text || "…"}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {m.last_at ? getTimeSince(m.last_at) + " ago" : ""}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Kitchen Chat Drawer (one order thread) */}
      <Sheet
        open={!!chatOrderId}
        onOpenChange={(o) => {
          if (!o) {
            // Clear unread for the closed thread
            setMessageOrders((prev) => prev.map((m) => m.order_id === chatOrderId ? { ...m, unread: 0 } : m));
            setChatOrderId(null);
          }
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b bg-white shrink-0">
            <SheetTitle className="text-base flex items-center gap-2">
              <MessageCircle className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />
              Reply to Customer
            </SheetTitle>
            <p className="text-[11px] text-slate-500">
              Order #{chatOrderId ? chatOrderId.slice(0, 8) : ""}
            </p>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            {chatOrderId && (
              <OrderChatPanel
                orderId={chatOrderId}
                viewer="staff"
                senderName="Store"
                authHeaders={getAuthHeaders()}
                webSocketMessage={lastMessage}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function KitchenDashboard() {
  return (
    <WebSocketProvider>
      <KitchenContent />
    </WebSocketProvider>
  );
}
